/**
 * samMaskMath.js — ЧИСТАЯ МАТЕМАТИКА переноса маски MedSAM на срез тома.
 * [11.1.54, по слову владельца «давай займёмся печенью»]
 *
 * ЗАЧЕМ ОТДЕЛЬНЫМ МОДУЛЕМ. Ноль импортов: ни cornerstone, ни window, ни
 * журнала. Значит проверяется числами в node — а проверять здесь надо именно
 * числа. Ошибка в масштабе или в порядке осей положит маску печени на чужой
 * срез или зеркально, и на экране это будет выглядеть как «сеть плохо
 * работает», хотя сеть отработала верно.
 *
 * ЧТО ИМЕННО СХОДИТСЯ НЕ САМО СОБОЙ.
 *
 * 1. РАЗМЕР. Декодер зовётся с orig_im_size = 1024×1024 (aiModule.js:713),
 *    то есть маска приходит НЕ в сетке среза, и масштабировать надо всегда.
 *    Но КОНКРЕТНЫЙ размер здесь не предполагается: он приходит доводами mw и
 *    mh. Ранее в этом пояснении стояло «всегда 1024×1024» — неверно: пока
 *    размер угадывался корнем из трети длины ответа, наружу выходило 591×591
 *    (см. [11.1.60] в aiModule). Теперь он берётся из dims тензора и может
 *    быть любым, в том числе неквадратным.
 *
 * 2. СПОСОБ. Ближайший сосед, не сглаживание. Маска двоичная: любое
 *    усреднение даёт полутона, а порог по ним съедает край печени на
 *    полпикселя внутрь или наружу. При уменьшении 1024 → 512 берём не одну
 *    точку, а МАКСИМУМ по блоку: тонкий язычок печени толщиной в один пиксель
 *    маски иначе пропадает совсем.
 *
 * 3. НОМЕР СРЕЗА. По журналу владельца нормаль осевого окна равна
 *    [0, 0, -1] — счёт срезов у окна идёт с противоположного конца тома.
 *    Поэтому номер у окна НЕ СПРАШИВАЕМ вовсе: срез определяется переводом
 *    точки взгляда в индексы вокселей (worldToIndex учитывает поворот серии).
 *    Здесь — только проверка и округление результата.
 */

/**
 * Маска MedSAM (mw×mh) → маска среза (tw×th), ближайшим соседом с максимумом
 * по блоку.
 *
 * @param {ArrayLike<number>} маска — двоичная, длиной mw*mh
 * @param {number} mw @param {number} mh — размер маски (у MedSAM 1024×1024)
 * @param {number} tw @param {number} th — размер среза тома
 * @returns {{ок: true, маска: Uint8Array}|{ок: false, причина: string}}
 */
export function масштабироватьМаску(маска, mw, mh, tw, th) {
  if (!маска || маска.length !== mw * mh) {
    return { ок: false, причина: `маска ${маска?.length ?? 0} не сходится с ${mw}×${mh} = ${mw * mh}` };
  }
  if (!(tw > 0 && th > 0)) return { ок: false, причина: 'размер среза не задан' };
  const из = new Uint8Array(tw * th);
  // Сколько пикселей маски приходится на один пиксель среза. При увеличении
  // (маска меньше среза) блок меньше единицы — берём ровно одну точку.
  const блокX = Math.max(1, Math.floor(mw / tw));
  const блокY = Math.max(1, Math.floor(mh / th));
  for (let y = 0; y < th; y++) {
    const y0 = Math.min(mh - 1, Math.floor((y * mh) / th));
    for (let x = 0; x < tw; x++) {
      const x0 = Math.min(mw - 1, Math.floor((x * mw) / tw));
      let есть = 0;
      for (let бy = 0; бy < блокY && !есть; бy++) {
        const yy = y0 + бy; if (yy >= mh) break;
        const строка = yy * mw;
        for (let бx = 0; бx < блокX; бx++) {
          const xx = x0 + бx; if (xx >= mw) break;
          if (маска[строка + xx]) { есть = 1; break; }
        }
      }
      из[y * tw + x] = есть;
    }
  }
  return { ок: true, маска: из };
}

/**
 * Номер среза тома по дробному индексу из worldToIndex.
 *
 * Отдельной функцией, потому что «округлить» здесь недостаточно: точка взгляда
 * может стоять на полшага за краем тома (камера смотрит на границу), и
 * округление вверх даст индекс вне массива — запись ушла бы в чужую память
 * или в никуда.
 *
 * @param {number} дробный — третья компонента worldToIndex
 * @param {number} nz — число срезов тома
 * @param {number} допуск — насколько можно выйти за край и быть притянутым
 */
export function номерСреза(дробный, nz, допуск = 0.75) {
  if (!Number.isFinite(дробный) || !(nz > 0)) {
    return { ок: false, причина: 'индекс среза не вычислился' };
  }
  // `|| 0` убирает минус-ноль: Math.round(-0.4) даёт -0, и хотя как индекс
  // массива он равен нулю, в журнал и в подпись он попал бы как «-0».
  const k = Math.round(дробный) || 0;
  if (k >= 0 && k < nz) return { ок: true, k };
  if (k < 0 && дробный > -1 - допуск) return { ок: true, k: 0, притянут: true };
  if (k >= nz && дробный < nz + допуск) return { ок: true, k: nz - 1, притянут: true };
  return { ок: false, причина: `срез ${k} вне тома (0…${nz - 1})` };
}

/**
 * Двоичная маска среза → индексы вокселей тома, которые надо пометить.
 *
 * Порядок осей в labelmap-томе Cornerstone: индекс = (k*ny + y)*nx + x.
 * Тот же порядок используют экспорт NIfTI и импорт маски в этом проекте —
 * второго соглашения не заводим.
 *
 * @param {ArrayLike<number>} маскаСреза — длиной nx*ny
 * @param {number[]} dims — [nx, ny, nz] тома маски
 * @param {number} k — номер среза
 * @returns {{ок: true, индексы: Int32Array}|{ок: false, причина: string}}
 */
export function индексыВТоме(маскаСреза, dims, k) {
  const [nx, ny, nz] = dims || [];
  if (!(nx > 0 && ny > 0 && nz > 0)) return { ок: false, причина: 'у тома маски нет размеров' };
  if (!(k >= 0 && k < nz)) return { ок: false, причина: `срез ${k} вне тома (0…${nz - 1})` };
  if (!маскаСреза || маскаСреза.length !== nx * ny) {
    return { ок: false, причина: `срез ${маскаСреза?.length ?? 0} не сходится с ${nx}×${ny} = ${nx * ny}` };
  }
  let сколько = 0;
  for (let i = 0; i < маскаСреза.length; i++) if (маскаСреза[i]) сколько++;
  const индексы = new Int32Array(сколько);
  const основа = k * ny * nx;
  let j = 0;
  for (let y = 0; y < ny; y++) {
    const строкаМ = y * nx;
    const строкаТ = основа + y * nx;
    for (let x = 0; x < nx; x++) {
      if (маскаСреза[строкаМ + x]) индексы[j++] = строкаТ + x;
    }
  }
  return { ок: true, индексы };
}

/**
 * Объём помеченного в миллилитрах. spacing в миллиметрах, 1 мл = 1000 мм³.
 * @param {number} вокселей @param {number[]} spacing
 */
export function объёмМлПоВокселям(вокселей, spacing) {
  const s = spacing || [1, 1, 1];
  const мм3 = Math.abs(s[0] * s[1] * s[2]);
  return (вокселей * мм3) / 1000;
}

/**
 * [11.1.61] СТОРОЖ ПЛОЩАДИ: не захватила ли сеть всё тело.
 *
 * ЗАЧЕМ. На срезе 404 у владельца сеть доложила IoU 0,9326 — и обвела ВСЁ
 * ТУЛОВИЩЕ: 139 308 вокселей на холсте против 9 440 на соседнем срезе, 59,5 мл
 * против 4,0. Уверенность модели в себе (IoU) правильность НЕ означает, и
 * признаком качества служить не может: сеть уверенно обвела не то.
 *
 * Признак, который годится, — доля площади среза. Печень на аксиальном срезе
 * занимает единицы-десятки процентов; всё тело — сорок и больше. Это не
 * диагностика, а грубая проверка на здравый смысл, и именно поэтому она
 * надёжна: ошибка «захватил всё» отличается от правды не на проценты, а в
 * разы.
 *
 * ПОЧЕМУ ПОРОГ 35%. Ниже ставить нельзя: печень на самых широких срезах
 * доходит до четверти площади, а на срезах, где рядом селезёнка и почки,
 * законная разметка бывает и больше. Выше — теряется смысл: захват тела у
 * владельца дал 53%. Порог не запрещает, а ПРЕДУПРЕЖДАЕТ: решает человек.
 *
 * @param {number} вокселей — сколько помечено на срезе
 * @param {number} nx @param {number} ny — размеры среза
 * @param {number} порог — доля площади, выше которой предупреждаем
 * @returns {{подозрительно: boolean, доля: number, процент: string}}
 */
export function похоже_на_всё_тело(вокселей, nx, ny, порог = 0.35) {
  const площадь = Math.max(1, (nx || 0) * (ny || 0));
  const доля = Math.max(0, вокселей || 0) / площадь;
  return { подозрительно: доля > порог, доля, процент: (доля * 100).toFixed(1) };
}

/**
 * [12.0.52] Окно показывает «осевой» срез ТОМА (плоскость i–j)? MedSAM
 * кодирует срез k = const — то есть плоскость индексов i и j. Щелчок в боковом
 * или лобовом окне MPR даёт точку (i, j) и номер k из фокуса ЭТОГО окна —
 * середины бокового вида, а не места щелчка; точка ложилась на чужой срез.
 * Признак: нормаль плоскости окна параллельна оси k тома.
 * @param {number[]} нормаль — viewPlaneNormal камеры окна
 * @param {number[]} direction — 9 чисел: оси i, j, k тома подряд
 */
export function окноПоОсиK(нормаль, direction) {
  if (!нормаль || !direction || direction.length < 9) return false;
  const k = [direction[6], direction[7], direction[8]];
  const дл = Math.hypot(...k) * Math.hypot(нормаль[0], нормаль[1], нормаль[2]);
  if (!(дл > 0)) return false;
  return Math.abs((k[0] * нормаль[0] + k[1] * нормаль[1] + k[2] * нормаль[2]) / дл) > 0.99;
}

/**
 * [12.0.52] Все точки на одном срезе, и это тот срез, что сейчас в окне?
 * Прежде срез брался из окна в момент «Запуска», а точки — из любых
 * срезов, на которых врач щёлкал: пролистал между щелчками — точки одного
 * среза уходили в сеть вместе с картинкой другого.
 * @param {Array<{k:number}>} точки
 * @param {number} kОкна
 * @returns {{ok:true}|{ok:false, причина:string}}
 */
export function точкиНаСрезе(точки, kОкна) {
  const срезы = [...new Set((точки || []).map((т) => т.k))];
  if (срезы.some((k) => !Number.isFinite(k))) return { ok: false, причина: 'у точки нет номера среза — поставьте точки заново' };
  if (срезы.length > 1) return { ok: false, причина: `точки стоят на разных срезах (${срезы.join(', ')}) — сеть обводит один срез; очистите точки и поставьте их на одном` };
  if (срезы.length === 1 && срезы[0] !== kОкна) return { ok: false, причина: `точки поставлены на срезе ${срезы[0]}, а в окне сейчас срез ${kОкна} — вернитесь на срез с точками или поставьте их заново` };
  return { ok: true };
}
